<template>
  <div>
    <div
      v-if="level == 1"
      class="lego-dropdown lego-input lego-tree-dropdown"
      v-click-outside="toggleItemOuter"
      ref="scpmTreeCheckboxDropdown"
    >
      <!-- Input dropdown -->
      <div
        class="lego-dropdown__title"
        :class="{
          'is-item-showing': isItemShowing,
          'disabled-dropdown': disabled
        }"
        @click="toggleSelect"
      >
        <div class="lego-dropdown__selected" :style="dropdownTitleStyle">
          <div
            class="lego-dropdown__selected-multiple"
            v-if="handleSelected && !isSelectedWholeItem"
          >
            <span v-lego-tooltip:ellipsis="handleSelected.fullText">
              {{ handleSelected.firstText }}
            </span>
            <span>{{ handleSelected.etc }}</span>
          </div>
          <span v-else :class="{ isSelectedWholeItem: 'lego-checkbox__label' }">{{
            selectedPlaceholder ? selectedPlaceholder : $t('label.all')
          }}</span>
        </div>

        <lego-icon-wrapper class="lego-dropdown__title-icon">
          <lego-icon
            class="lego-dropdown__icon"
            icon-type="df"
            icon="arrow_down"
            size="16px"
            :style="caretStyle"
          />
        </lego-icon-wrapper>
      </div>
      <!-- Dropdown menu -->
      <div class="lego-dropdown__downwrap" v-if="isItemShowing">
        <div class="lego-dropdown__tags">
          <div class="auto-complete__search-box">
            <lego-text-field
              ref="filterInput"
              :placeholder="searchPlaceholder"
              :modelValue="searchText"
              searchable
              counter="100"
              :isCounter="false"
              :mutateInput="mutateInput"
              :disableInputMutation="disableInputMutation"
              @update:modelValue="filterTextOnInput"
            />
          </div>

          <div v-show="errorMessage" class="error-message">
            {{ errorMessage }}
          </div>

          <div class="search-result" v-if="searchText">
            {{ this.$t('label.searchResultParam', ['(' + searchItems.length + ')']) }}
          </div>

          <div v-if="allItems === undefined || !allItems.length" class="no-result">
            {{ t('label.noSearchResult') }}
          </div>

          <template v-else>
            <div v-if="multiSelect" class="lego-dropdown__selectall-wrap">
              <div
                v-if="!searchText"
                class="lego-dropdown__item lego-dropdown__selectall"
                :class="{ 'lego-dropdown__checked': isCheckAll }"
                @click="toggleCheckAll()"
              >
                <lego-checkbox
                  :indeterminate="indeterminate"
                  :checked="isCheckAll"
                  @click="toggleCheckAll()"
                  >{{ $t('label.all') }}
                </lego-checkbox>
              </div>
            </div>

            <c-scroll style="max-height: 306px">
              <div v-for="(item, index) in allItems" :key="index" class="accordion-list-item">
                <div
                  @click.stop="toggleItem(item)"
                  :class="{ 'lego-dropdown__checked': item.check }"
                  class="accordion-list-item__main"
                >
                  <template v-if="!item.childItems">
                    <lego-checkbox
                      style="width: 100%"
                      v-if="multiSelect"
                      v-model="item.check"
                      @update:modelValue="checkItem(item)"
                    >
                      <div
                        v-lego-tooltip:ellipsis="item.metricName"
                        v-html="getOptionLabel(item)"
                      ></div>
                    </lego-checkbox>
                    <c-label v-else ellipsis style="width: initial">
                      {{ item.text }}
                    </c-label>
                  </template>
                  <div
                    v-else
                    v-lego-tooltip:ellipsis="item.metricName"
                    class="item-title-sec ellipsis"
                  >
                    {{ item.text }}
                    <span v-if="isNearTheEnd(item)">({{ item.childItems.length }})</span>
                  </div>
                  <lego-icon
                    v-if="item.childItems"
                    style="padding: 2px"
                    iconType="df"
                    :size="16"
                    :icon="item.expand ? 'arrow_up' : 'arrow_down'"
                  />
                </div>

                <div v-if="item.expand">
                  <scpm-tree-checkbox-dropdown
                    v-if="item.childItems"
                    :level="level + 1"
                    :items="item.childItems"
                    :multiSelect="multiSelect"
                    :propSearchText="searchText || propSearchText"
                  />
                </div>
              </div>
            </c-scroll>
          </template>
        </div>
      </div>
    </div>

    <template v-else>
      <div v-for="(item, index) in allItems" :key="index" class="accordion-list-item">
        <div
          @click.stop="toggleItem(item)"
          class="accordion-list-item__main"
          :class="{ 'lego-dropdown__checked': item.check }"
          :style="styleChild(item)"
        >
          <template v-if="!item.childItems">
            <lego-checkbox
              style="width: 100%"
              v-if="multiSelect"
              v-model="item.check"
              @update:modelValue="checkItem(item)"
            >
              <div v-lego-tooltip:ellipsis="item.metricName" v-html="getOptionLabel(item)"></div>
            </lego-checkbox>
            <c-label v-else ellipsis style="width: initial">
              {{ item.text }}
            </c-label>
          </template>
          <div v-else v-lego-tooltip:ellipsis="item.metricName" class="item-title-sec ellipsis">
            {{ item.text }}
            <span v-if="isNearTheEnd(item)">({{ item.childItems.length }})</span>
          </div>

          <lego-icon
            v-if="item.childItems"
            style="padding: 2px"
            iconType="df"
            :size="16"
            :icon="item.expand ? 'arrow_up' : 'arrow_down'"
          />
        </div>
        <div v-if="item.expand">
          <scpm-tree-checkbox-dropdown
            v-if="item.childItems"
            :level="level + 1"
            :items="item.childItems"
            :multiSelect="multiSelect"
            :propSearchText="searchText || propSearchText"
          />
        </div>
      </div>
    </template>
  </div>
</template>

<script>
import i18nMixin from '@/mixins/i18n.mixin.js';
import cScroll from '../../cmp/cScroll/cScroll.vue';
import ScpmSubTreeDropdown from './ScpmSubTreeDropdown';
import NullWidget from '@/containers/widget/components/NullWidget.vue';

import { convertFlatMetricsToTree, flatMetricsTree } from '@/containers/utils/utils';
import { mapActions, mapState } from 'vuex';
import { isEqual, cloneDeep } from 'lodash';

export default {
  mixins: [i18nMixin],
  name: 'scpm-tree-checkbox-dropdown',
  components: { cScroll, ScpmSubTreeDropdown, NullWidget },
  emits: ['update:modelValue', 'is-scroll', 'changeHeightDownwrap'],
  props: {
    items: {
      type: Array,
      required: true
    },
    modelValue: {
      type: [Object, String, Array],
      required: false
    },
    level: {
      type: Number,
      default: 1
    },
    searchKey: {
      type: String,
      default: ''
    },
    needResetMetrics: {
      type: Boolean,
      default: false
    },
    searchPlaceholder: {
      type: String,
      default: ''
    },
    mutateInput: { type: Function, default: null },
    multiSelect: { type: Boolean, default: true },
    selectedMetrics: {
      type: Array,
      default: () => [],
      required: false
    },
    disabled: { type: Boolean, default: false },
    selectedPlaceholder: {
      type: String,
      default: ''
    },
    propSearchText: {
      type: String,
      default: ''
    },
    disableInputMutation: {
      type: Boolean,
      default: false
    },
    maxSelect: { type: [Number, String], default: 100 },
    messageErrorMaxSelect: { type: String, default: '' },
    isPerformanceItemOfCustomCommon: { type: Boolean, default: false }
  },

  data() {
    return {
      searchItems: [],
      searchText: '',
      allItems: cloneDeep(this.items),
      isCheckAll: false,
      textToolTip: '',
      hasError: false,
      isItemShowing: false,
      isMultiSelect: false,
      countSelected: ''
    };
  },

  computed: {
    ...mapState({
      metrics: (state) => state.metric.metrics,
      justResetMetrics: (state) => state.metric.justReset,
      errorMessage: (state) => state.metric.errorMessage,
      messageErrorMaxSelectState: (state) => state.metric.messageErrorMaxSelectState
    }),

    isScroll() {
      return this.$store.state.commonStore.scroll;
    },

    flatOriginItems() {
      return flatMetricsTree(this.items);
    },

    caretStyle() {
      let style = {};
      if (this.isItemShowing) {
        style['transform'] = 'rotate(-180deg)';
      }
      style['transition-duration'] = '0.3s';

      return style;
    },

    flatAllItems() {
      return flatMetricsTree(this.allItems);
    },

    countSelectedInFlatItems() {
      return this.metrics.filter((item) => item.check).length;
    },

    isSelectedWholeItem() {
      if (!this.multiSelect && this.flatOriginItems.length) return false;
      return this.flatOriginItems && this.flatOriginItems.length === this.metrics.length;
    },

    dropdownTitleStyle() {
      if (this.metrics.length) {
        return { color: '#000' };
      }

      return { color: '#a5a7ad' };
    },

    handleSelected() {
      if (!this.metrics.length) return false;
      const firstText = this.metrics[0].metricName || '';
      const etc =
        this.metrics.length > 1 ? this.$t('label.andOther', [this.metrics.length - 1]) : '';

      const fullText = etc ? firstText + ' ' + etc : firstText;

      return { firstText, etc, fullText };
    },

    indeterminate() {
      return (
        !!this.countSelectedInFlatItems && this.countSelectedInFlatItems < this.flatAllItems.length
      );
    }
  },

  watch: {
    isScroll(val) {
      if (val && this.isItemShowing) {
        this.$emit('is-scroll', true);
      }
    },
    isItemShowing(value) {
      if (value) {
        this.handleExpand(this.allItems);
        this.expandFirstTree();
      } else {
        this.$emit('is-scroll', false);
      }
    },
    selectedMetrics: {
      handler(val) {
        if (!this.multiSelect && val && val.length && !this.isSelectedItem(val[0])) {
          this.resetSelectMetric(true);
          this.setCheck(val[0], true);
        }
      },
      deep: true,
      immediate: true
    },
    searchText(value) {
      if (!value) {
        this.searchItems = this.flatOriginItems;
        this.allItems = convertFlatMetricsToTree(
          this.searchItems,
          this.metrics,
          false,
          this.isPerformanceItemOfCustomCommon
        );
        this.handleExpand(this.allItems);
        this.expandFirstTree();
      } else {
        this.setErrorMessage('');
        this.searchItems = this.flatOriginItems.filter((item) =>
          item[this.searchKey].toLowerCase().includes(value.toLowerCase())
        );
        this.allItems = convertFlatMetricsToTree(
          this.searchItems,
          this.metrics,
          false,
          this.isPerformanceItemOfCustomCommon
        );
        for (const item of this.allItems) {
          if (item.childItems) {
            this.expandAll(item);
            break;
          }
        }
      }

      this.changeHeightDownwrap();
      this.checkIsCheckAll();
    },
    items(newItems) {
      if (newItems.length) {
        this.allItems = newItems;
      }
    },
    justResetMetrics() {
      this.searchText = '';
      this.collapseAll();
      this.expandFirstTree();
    },
    metrics: {
      handler(newValue) {
        if (!newValue.length) {
          this.updateCheckAll(false);
        }

        if (newValue.length > this.maxSelect) {
          this.setErrorMessage(this.messageErrorMaxSelect);
        } else {
          this.setErrorMessage('');
        }

        const selectedMetrics = this.metrics.map((metric) => ({
          key: metric.metricKey,
          type: metric.objectType,
          ...metric
        }));
        if (!this.multiSelect && newValue.length) this.resetAllCheckItem(newValue[0]);
        this.$emit('update:modelValue', selectedMetrics);
        this.checkIsCheckAll();
      },
      immediate: true,
      deep: true
    },
    needResetMetrics: {
      handler(newValue) {
        if (newValue) this.resetSelectMetric();
      }
    }
  },

  unmounted() {
    if (this.level === 1) {
      this.resetSelectMetric();
    }
  },

  mounted() {
    if (this.needResetMetrics) this.resetSelectMetric();
    this.expandFirstTree();
  },
  created() {
    if (this.level === 1) {
      if (!Array.isArray(this.modelValue)) return;
      const newItem = this.modelValue.map((item) => {
        delete item.key;
        delete item.type;
        return item;
      });
      this.selectMetricAction(newItem);
    } else {
      this.allItems = this.allItems.map((item) => {
        const isCheck = this.metrics.some(
          ({ metricKey, objectType }) =>
            metricKey === item.metricKey && this.compareObjectType(objectType, item.objectType)
        );
        return { ...item, check: isCheck };
      });
    }
  },

  methods: {
    ...mapActions('metric', [
      'syncSelectMetric',
      'resetSelectMetric',
      'selectMetricAction',
      'setErrorMessage',
      'setMessageForMaxSelect'
    ]),
    compareObjectType(value, valueCompare) {
      if (!value && !valueCompare) return true;
      return value === valueCompare;
    },
    validateSelected(options) {
      if (options.length > this.maxSelect) {
        this.setErrorMessage(this.messageErrorMaxSelect);
        return false;
      }
      this.setErrorMessage('');
      return true;
    },
    getOptionLabel(item) {
      let itemText = item.text || item.metricName || '';
      const textInputSearch = this.searchText || this.propSearchText;
      itemText = this.$util.escapeHTML(itemText);
      if (textInputSearch === '') return itemText;
      const regex = this.$util
        .escapeHTML(textInputSearch)
        .replace(/[!@#$%^&*()+=\-[\]\\';,./{}|":<>?~_]/g, '\\$&');

      return itemText.replace(
        new RegExp(regex, 'ig'),
        (w) => `<span style='color:#2c59d9'>${w}</span>`
      );
    },
    handleExpand(items) {
      for (const item of items) {
        item.expand = this.metrics.some(
          ({ metricKey, objectType }) =>
            metricKey === item.metricKey && this.compareObjectType(objectType, item.objectType)
        );
        if (!item.childItems || !item.childItems.length) continue;
        this.handleExpand(item.childItems);
        item.expand = item.childItems.some((child) => !!child.expand);
      }
    },

    filterTextOnInput(value) {
      this.searchText = value;
    },

    checkIsCheckAll() {
      this.isCheckAll =
        !!this.metrics.length &&
        this.flatAllItems.every(
          (item) =>
            this.metrics.findIndex(
              (m) =>
                m.metricKey === item.metricKey &&
                this.compareObjectType(m.objectType, item.objectType)
            ) > -1
        );
    },

    resetAllCheckItem(item) {
      this.allItems = this.allItems.map((e) => {
        if (!item.metricKey) return e;
        return {
          ...e,
          check:
            e.metricKey === item.metricKey && this.compareObjectType(e.objectType, item.objectType)
        };
      });
    },

    styleChild() {
      return { 'padding-left': this.level * 12 + 'px' };
    },

    toggleItemOuter() {
      this.isItemShowing = false;
    },

    toggleSelect() {
      if (this.disabled) return;
      this.setMessageForMaxSelect(this.messageErrorMaxSelect);
      this.isItemShowing = !this.isItemShowing;
    },

    changeHeightDownwrap() {
      this.$nextTick(() => {
        this.$emit('changeHeightDownwrap');
      });
    },

    toggleItem(item) {
      item.expand = !item.expand;
      if (item.childItems) {
        this.changeHeightDownwrap();
        return;
      }
      if (!this.multiSelect) {
        if (!this.isSelectedItem(item)) {
          this.resetSelectMetric(true);
          this.setCheck(item, true);
        }
      } else {
        item.check = !item.check;
        this.checkItem(item);
      }
    },

    expandAll(item, expandStatus = true) {
      if (item.childItems) {
        item.expand = expandStatus;
        item.childItems.forEach((item) => this.expandAll(item, expandStatus));
      }
    },

    expandFirstTree() {
      if (this.level > 1 || !this.allItems.length) return;
      if (this.allItems.some((allItem) => !!allItem.expand)) return;

      this.expandAll(this.allItems[0]);
    },

    collapseAll() {
      if (!this.allItems.length) return;

      for (const item of this.allItems) {
        if (item.childItems) {
          this.expandAll(item, false);
        }
      }
    },

    isNearTheEnd(item) {
      if (item.childItems) return !item.childItems.some((sub) => sub.childItems);

      return false;
    },

    toggleCheckAll() {
      if (!this.validateSelected(this.flatAllItems)) return;
      this.isCheckAll = !this.isCheckAll;
      this.updateCheckAll(this.isCheckAll);
    },

    updateCheckAll(checkStatus) {
      const newAllItem = this.allItems.map((item) => {
        return this.setCheck(item, checkStatus);
      });
      this.allItems = cloneDeep(newAllItem);
    },

    setCheck(item, check) {
      if (item.childItems) {
        item.childItems.forEach((sub) => this.setCheck(sub, check));
      } else {
        item.check = check;
        this.syncSelectMetric(item);
      }

      return item;
    },

    checkItem(item) {
      if (this.metrics.length == this.maxSelect) {
        item.check = false;
        this.setErrorMessage(this.messageErrorMaxSelectState);
      } else {
        this.setErrorMessage('');
      }

      this.setCheck(item, item.check);
    },

    isSelectedItem(item) {
      const index = this.metrics.findIndex(
        (i) =>
          i.metricKey === item.metricKey && this.compareObjectType(i.objectType, item.objectType)
      );

      return index > -1;
    }
  }
};
</script>

<style lang="scss" scoped>
.error-message {
  font-size: 12px;
  color: #fb4b57;
  padding-left: 12px;
  line-height: 12px;
  margin-bottom: 4px;
}

.search-result {
  padding-left: 12px;
  font-size: 12px;
  color: #999999;
  line-height: 1;
  margin-bottom: 5px;
}
.no-result {
  cursor: default;
  padding: 25px 0;
  text-align: center;
  font-size: 11px;
  color: #858585;
}
.lego-dropdown {
  height: 32px;
  line-height: 24px;
  font-size: 14px;

  .auto-complete__search-box {
    padding: 12px 12px 5px 12px;
  }

  &__title {
    border-radius: 2px;
    &:hover {
      border-color: #2c59d9;
    }

    &.is-item-showing {
      border-color: $lego-dropdown__title__border--focus;
    }
    &.disabled-dropdown {
      background-color: #f4f4f6;
      border: 1px solid #e3e4e8;
      cursor: default;
    }
  }

  &__selected {
    display: flex;
    color: #a5a7ad;
    padding: 0 12px;

    span {
      display: inline-block;
      @include ellipsis;
    }

    &-multiple {
      display: flex;
      width: 100%;
      justify-content: flex-start;

      :last-child {
        padding-left: 4px;
        flex-shrink: 0;
      }
    }
  }

  &__downwrap {
    width: 100%;
  }

  &__tags {
    background-color: #ffffff;
    border: 1px solid #364fc7;
  }

  &__item {
    padding: 0 12px 0 12px;

    &.item-checked,
    &:hover {
      background-color: #eff1fb;

      .lego-dropdown__label {
        .lego-checkbox__label {
          color: #2f3237;
          font-weight: 400;
        }
      }
    }
  }

  &__selectall {
    display: flex !important;
    border: 0;
    border-bottom: 1px solid #e4e5e7;
    line-height: 24px;
    min-height: 34px;
    max-height: 48px;
    font-weight: 700;
    align-items: center;
    :deep.lego-dropdown__item .lego-checkbox__checked + .lego-checkbox__label,
    .lego-checkbox__label {
      color: #2f3237 !important;
      padding-top: 1px;
    }
    .lego-checkbox {
      width: 100%;
    }
    &:hover {
      background-color: #eff1fb !important;
    }
  }

  &__items {
    // margin-bottom: 42px;
    max-height: initial;
    border: 0;
    overflow-x: hidden;
    overflow-y: hidden;
  }

  &__selectall-wrap {
    padding: 0 0px;
  }

  &__item:hover {
    background-color: transparent;
  }

  &__apply-wrap {
    padding: 0 10px;
  }

  &__apply {
    border: 0;
    border-top: 1px solid #a5a7ad;
  }

  &__label {
    flex: 0 1 auto;
    display: block;
    justify-content: flex-start;
    align-items: stretch;
    align-content: stretch;
  }

  &__checked {
    background-color: #eff1fb !important;
    color: $color--primary;
  }

  :deep .lego-button--default:not(.lego-button--loading):hover {
    background-color: #2b3f9f !important;
  }

  :deep .lego-checkbox__label {
    display: block;
    // width: calc(100% - 10px);
    white-space: nowrap;
    text-overflow: ellipsis;
    overflow: hidden;
    color: #2f3237;
    padding-top: 1px;

    em {
      display: block;
      font-style: normal;
      @include ellipsis;
    }
  }

  :deep .lego-checkbox {
    display: block;
    flex: 0 1 auto;
    align-items: flex-start;
    padding: 0 0 0 15px;
  }

  :deep .lego-checkbox__icon {
    position: absolute;
    top: 3px;
    left: 0;
  }

  .accordion-list-item {
    // border: 1px solid $color--ui08;
    background-color: $color--ui-white;

    &__main {
      height: 36px;
      display: flex;
      align-items: center;
      padding-right: 12px;
      padding-left: 12px;
      cursor: pointer;

      &:hover {
        background-color: #eff1fb !important;
        color: $color--primary;
      }

      .lego-checkbox,
      .lego-radio {
        flex: none;
        margin-right: 8px;

        &:hover {
          & + .item-title-sec {
            color: inherit;

            :deep & + .expand-icon {
              .lego-icon {
                color: rgba($color--ui01, 0.6);
              }
            }
          }
        }
      }

      .item-title-sec {
        flex: 1 1 auto;
        font-weight: 500 !important;
        @include font(14px);
      }

      .expand-icon {
        cursor: pointer;
        padding: 10px;

        :deep .lego-icon {
          vertical-align: middle;
        }

        :deep &.open {
          .lego-icon {
            transform: rotate(-180deg);
          }
        }
      }
    }

    &__expand {
      border-top: 1px solid $color--ui08;
      margin: 0 20px;
      padding: 8px 12px;
    }
  }
}
</style>
